/**
 * Labiarge reference-currency display.
 *
 * The store charges in TRY and only in TRY. Shopify presents local currencies
 * only when the primary gateway is Shopify Payments or Adyen, and Labiarge is on
 * iyzico/PayTR. So this module never *replaces* a price: it appends an
 * approximate figure beside the real one ("55.000,00TL  ≈ €1,008"), which is the
 * honest shape for a conversion the checkout will not honour.
 *
 * Rates come from snippets/labiarge-fx-rates.liquid, rendered server-side from a
 * shop metafield. No client-side FX request, so nothing to fail or rate-limit
 * and no flash of an unconverted price.
 *
 * Three conditions silently disable the whole thing, each on purpose:
 *   - selected currency is the shop currency (nothing to convert),
 *   - the rates blob is missing (metafield never written),
 *   - the rates are older than STALE_AFTER_DAYS.
 * A stale reference price is worse than no reference price, because a visitor
 * who acts on it is the one who pays for the drift.
 */

const RATES_ID = 'labiarge-fx-rates';
const CURRENCY_KEY = 'labiarge:localization:currency';
const CHANGE_EVENT = 'labiarge:currency:change';
const MARKER_ATTR = 'data-labiarge-fx';
const STALE_AFTER_DAYS = 7;

/**
 * Price surfaces we annotate. `product-price` covers the PDP *and* product
 * cards, which Horizon renders with the same element, so a collection grid gets
 * a figure under every card. Narrow to
 * `product-price:not(product-card product-price)` if that ever reads as busy.
 */
const TARGET_SELECTOR = 'product-price, [data-cart-subtotal]';

/** @type {{base: string, updated_at: string, per_unit: Record<string, number>} | null} */
let rates = null;
/** @type {MutationObserver | null} */
let observer = null;

/**
 * @param {string} key
 * @returns {string | null}
 */
function readStore(key) {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

/** Parse the rates blob, returning null when it is absent or too old. */
function loadRates() {
  const node = document.getElementById(RATES_ID);
  if (!node) return null;

  let parsed;
  try {
    parsed = JSON.parse(node.textContent || '');
  } catch {
    return null;
  }
  if (!parsed?.per_unit || !parsed?.updated_at) return null;

  const age = (Date.now() - Date.parse(parsed.updated_at)) / 86_400_000;
  if (!Number.isFinite(age) || age > STALE_AFTER_DAYS) return null;

  return parsed;
}

/**
 * Pull a number out of a rendered money string.
 *
 * There is no raw-cents attribute on Horizon's price markup, so the displayed
 * string is all we have. The ambiguous case is a single separator: "55,000" is
 * fifty-five thousand while "55,00" is fifty-five. Decided by whether exactly
 * three digits follow, which is the one rule that holds in both the comma- and
 * dot-grouped formats Shopify emits.
 *
 * @param {string} text
 * @returns {number | null}
 */
export function parseMoney(text) {
  const cleaned = (text || '').replace(/[^\d.,]/g, '');
  if (!cleaned) return null;

  const lastDot = cleaned.lastIndexOf('.');
  const lastComma = cleaned.lastIndexOf(',');
  const cut = Math.max(lastDot, lastComma);

  let normalized;
  if (cut === -1) {
    normalized = cleaned;
  } else if (cleaned.length - cut - 1 === 3) {
    // Three trailing digits. A thousands separator, not a decimal point.
    normalized = cleaned.replace(/[.,]/g, '');
  } else {
    normalized = cleaned.slice(0, cut).replace(/[.,]/g, '') + '.' + cleaned.slice(cut + 1);
  }

  const value = Number.parseFloat(normalized);
  return Number.isFinite(value) ? value : null;
}

/**
 * @param {number} amount
 * @param {string} currency
 * @returns {string}
 */
function formatMoney(amount, currency) {
  const locale = document.documentElement.lang || 'en';
  try {
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency,
      currencyDisplay: 'narrowSymbol',
      // A reference figure carries no cents. Quoting "€1,007.63" implies a
      // precision the checkout will not reproduce.
      maximumFractionDigits: 0,
    }).format(amount);
  } catch {
    return `${Math.round(amount)} ${currency}`;
  }
}

/** @returns {string} the shop currency when nothing else applies */
function selectedCurrency() {
  return readStore(CURRENCY_KEY) || rates?.base || '';
}

/** Remove every figure we previously inserted. */
function clear(root = document) {
  for (const node of root.querySelectorAll(`[${MARKER_ATTR}]`)) node.remove();
}

/**
 * The amount a price block is actually advertising.
 *
 * A `product-price` holds several money strings at once, regular, sale, and
 * whichever of the two is hidden. So reading the block's whole textContent
 * concatenates their digits into nonsense (55,000 + 55,000 became
 * "€1,007,641,688,148" the first time this shipped). Read the first *visible*
 * leaf price instead, and only fall back to the block itself for simple targets
 * like the cart total, which hold exactly one figure.
 *
 * Returns the node too, because that is where the figure has to be inserted: a
 * `product-price` also carries the "Shipping calculated at checkout" line, so
 * appending to the container drops the conversion below it, detached from the
 * price it converts.
 *
 * @param {Element} element
 * @returns {{amount: number, node: Element} | null}
 */
function amountFor(element) {
  const leaves = element.matches('.price')
    ? [element]
    : Array.from(element.querySelectorAll('.price, .price-item'));

  for (const leaf of leaves) {
    if (leaf.closest('.price__hidden, [hidden]')) continue;
    if (typeof leaf.checkVisibility === 'function' && !leaf.checkVisibility()) continue;
    if (leaf.querySelector('.price, .price-item')) continue; // not a leaf after all
    const value = parseMoney(leaf.textContent || '');
    if (value) return { amount: value, node: leaf };
  }

  if (leaves.length) return null;

  // Simple target (the cart total). One figure, nothing to disambiguate.
  const value = parseMoney(element.textContent || '');
  return value ? { amount: value, node: element } : null;
}

/** Annotate one price element. */
function annotate(element, currency, perUnit) {
  const existing = element.querySelector(`[${MARKER_ATTR}]`);
  if (existing) existing.remove();

  // Read after removing our own annotation, or repeated runs would fold the
  // approximation back into the source value.
  const source = amountFor(element);
  if (!source || source.amount <= 0) return;

  const converted = source.amount / perUnit;
  if (!Number.isFinite(converted)) return;

  const span = document.createElement('span');
  span.className = 'labiarge-fx';
  span.setAttribute(MARKER_ATTR, '');
  span.textContent = `≈ ${formatMoney(converted, currency)}`;

  // Sit directly beneath the figure being converted. When the target *is* the
  // figure (the cart total) go inside it instead. `after()` there would put the
  // node outside `element`, where the next run's cleanup query cannot see it,
  // and every re-render would leave another copy behind.
  if (source.node === element) element.append(span);
  else source.node.after(span);
}

/** Apply (or remove) the annotation across the page. */
export function apply() {
  const currency = selectedCurrency();
  const perUnit = rates?.per_unit?.[currency];

  // Shop currency, no rates, or an unknown code, nothing to show.
  if (!rates || !perUnit || currency === rates.base) {
    clear();
    return;
  }

  // Our own insertions mutate the very nodes we watch; detaching first is the
  // simplest guarantee against an observer feedback loop.
  observer?.disconnect();
  for (const element of document.querySelectorAll(TARGET_SELECTOR)) {
    annotate(element, currency, perUnit);
  }
  observe();
}

/** Re-apply after Horizon re-renders a price (variant switch, cart update). */
function observe() {
  const targets = document.querySelectorAll(TARGET_SELECTOR);
  if (!targets.length) return;

  observer ||= new MutationObserver(() => {
    // Coalesce: a variant change rewrites several nodes in one tick.
    queueMicrotask(apply);
  });
  for (const target of targets) {
    observer.observe(target, { childList: true, characterData: true, subtree: true });
  }
}

rates = loadRates();
if (rates) {
  apply();
  document.addEventListener(CHANGE_EVENT, apply);
  // Cart drawer and section renders swap whole subtrees, so the elements we
  // observed can be replaced wholesale rather than mutated.
  document.addEventListener('shopify:section:load', () => {
    observer?.disconnect();
    apply();
  });
}
